import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, Send, Paperclip, Loader2, Bot, Clock, CheckCircle, AlertCircle, XCircle, User, ShieldCheck, Sparkles, FileIcon, Download, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const statusConfig = {
  open: { icon: AlertCircle, color: "text-amber-400", bg: "bg-amber-500/10", label: "Ouvert" },
  in_progress: { icon: Clock, color: "text-blue-400", bg: "bg-blue-500/10", label: "En cours" },
  resolved: { icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-500/10", label: "Resolu" },
  closed: { icon: XCircle, color: "text-slate-400", bg: "bg-slate-500/10", label: "Ferme" },
};

const priorityLabels = { low: "Basse", medium: "Moyenne", high: "Haute" };

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const autoResizeTextarea = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "44px";
      const newHeight = Math.min(Math.max(el.scrollHeight, 44), 300);
      el.style.height = newHeight + "px";
    }
  };

  useEffect(() => { autoResizeTextarea(); }, [newMessage]);

  useEffect(() => { fetchData(); }, [id]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ticketRes, msgRes] = await Promise.all([
        axios.get(`${API}/tickets/${id}`),
        axios.get(`${API}/tickets/${id}/messages`),
      ]);
      setTicket(ticketRes.data);
      setMessages(msgRes.data);
    } catch (err) {
      toast.error("Ticket introuvable");
      navigate("/app/tickets");
    } finally { setLoading(false); }
  };

  const handleSend = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;
    setSending(true);
    try {
      await axios.post(`${API}/tickets/${id}/messages`, {
        content: newMessage,
        attachments,
      });
      setNewMessage("");
      setAttachments([]);
      // Reset textarea height after sending
      if (textareaRef.current) textareaRef.current.style.height = "44px";
      await fetchData();
    } catch { toast.error("Erreur envoi message"); }
    finally { setSending(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/tickets/${id}/upload-attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments(prev => [...prev, res.data]);
      toast.success("Fichier attache");
    } catch { toast.error("Erreur upload fichier"); }
    finally { setUploading(false); fileInputRef.current.value = ""; }
  };

  const handleAiAnalyze = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/tickets/${id}/ai-analyze`);
      setAiAnalysis(res.data);
    } catch { toast.error("Erreur analyse IA"); }
    finally { setAiLoading(false); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/tickets/${id}`, { status: newStatus });
      setTicket(prev => ({ ...prev, status: newStatus }));
      toast.success("Statut mis a jour");
    } catch { toast.error("Erreur mise a jour"); }
  };

  const applySuggestion = (text) => {
    setNewMessage(text);
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("fr-FR", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
    </div>
  );

  if (!ticket) return null;

  const status = statusConfig[ticket.status] || statusConfig.open;
  const StatusIcon = status.icon;

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] animate-fade-in" data-testid="ticket-detail-page">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-[#2A3441] pb-4 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/app/tickets")} className="text-[#94A3B8] hover:text-white" data-testid="back-to-tickets-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour
          </Button>
        </div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" data-testid="ticket-title">
              <StatusIcon className={`h-5 w-5 ${status.color}`} />
              {ticket.title}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-sm">
              <Badge className={`${status.bg} ${status.color} border-0`}>{status.label}</Badge>
              <Badge variant="outline" className="border-[#2A3441] text-[#94A3B8]">{priorityLabels[ticket.priority] || ticket.priority}</Badge>
              <span className="text-[#64748b]">{formatDate(ticket.created_at)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <Button onClick={handleAiAnalyze} disabled={aiLoading} variant="outline"
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 hover:text-purple-300"
                  data-testid="ai-analyze-btn">
                  {aiLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                  Analyser IA
                </Button>
                <Select value={ticket.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-[140px] bg-[#0B0F19] border-[#2A3441] text-white text-sm" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151B2B] border-[#2A3441]">
                    <SelectItem value="open">Ouvert</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="resolved">Resolu</SelectItem>
                    <SelectItem value="closed">Ferme</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        </div>
      </div>

      {/* AI Analysis Panel */}
      {aiAnalysis && (
        <div className="flex-shrink-0 mb-4 bg-purple-500/5 border border-purple-500/20 rounded-xl p-4" data-testid="ai-analysis-panel">
          <div className="flex items-center gap-2 mb-3">
            <Bot className="h-5 w-5 text-purple-400" />
            <h3 className="text-purple-300 font-semibold text-sm">Analyse IA</h3>
            <Button variant="ghost" size="sm" onClick={() => setAiAnalysis(null)} className="ml-auto text-[#64748b] h-6 w-6 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid md:grid-cols-3 gap-3 text-sm mb-3">
            <div>
              <span className="text-[#64748b] block">Categorie</span>
              <span className="text-white">{aiAnalysis.categorie}</span>
            </div>
            <div>
              <span className="text-[#64748b] block">Urgence</span>
              <span className="text-white">{aiAnalysis.urgence}</span>
            </div>
            <div>
              <span className="text-[#64748b] block">Resume</span>
              <span className="text-white">{aiAnalysis.resume}</span>
            </div>
          </div>
          {aiAnalysis.suggestions?.length > 0 && (
            <div>
              <span className="text-[#64748b] text-xs block mb-2">Suggestions de reponse :</span>
              <div className="flex flex-col gap-2">
                {aiAnalysis.suggestions.map((s, i) => (
                  <button key={i} onClick={() => applySuggestion(s)}
                    className="text-left text-sm p-2 rounded-lg bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] hover:text-white hover:border-purple-500/50 transition-colors"
                    data-testid={`ai-suggestion-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-2 min-h-0" data-testid="messages-container">
        {/* Original ticket description as first "message" */}
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-[#0099CC]/20 flex items-center justify-center flex-shrink-0">
            <User className="h-4 w-4 text-[#0099CC]" />
          </div>
          <div className="flex-1 bg-[#151B2B] border border-[#2A3441] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-white text-sm font-medium">Ticket initial</span>
              <span className="text-[#64748b] text-xs">{formatDate(ticket.created_at)}</span>
            </div>
            <p className="text-[#94A3B8] text-sm whitespace-pre-wrap">{ticket.description}</p>
          </div>
        </div>

        {messages.map((msg) => {
          const isMine = (isAdmin && msg.sender_role === "admin") || (!isAdmin && msg.sender_role === "client");
          return (
            <div key={msg.id} className={`flex gap-3 ${isMine ? "flex-row-reverse" : ""}`} data-testid={`message-${msg.id}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                msg.sender_role === "admin" ? "bg-emerald-500/20" : "bg-[#0099CC]/20"
              }`}>
                {msg.sender_role === "admin" ? <ShieldCheck className="h-4 w-4 text-emerald-400" /> : <User className="h-4 w-4 text-[#0099CC]" />}
              </div>
              <div className={`flex-1 max-w-[75%] rounded-xl p-4 ${
                isMine ? "bg-[#0099CC]/10 border border-[#0099CC]/30" : "bg-[#151B2B] border border-[#2A3441]"
              }`}>
                <div className={`flex items-center gap-2 mb-1 ${isMine ? "flex-row-reverse" : ""}`}>
                  <span className="text-white text-sm font-medium">{msg.sender_name || (msg.sender_role === "admin" ? "Admin" : "Client")}</span>
                  <span className="text-[#64748b] text-xs">{formatDate(msg.created_at)}</span>
                </div>
                <p className="text-[#94A3B8] text-sm whitespace-pre-wrap">{msg.content}</p>
                {msg.attachments?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {msg.attachments.map((att, i) => (
                      <a key={i} href={`${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}${att.url}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs bg-[#0B0F19] rounded-lg px-2 py-1 text-[#0099CC] hover:text-white transition-colors">
                        <FileIcon className="h-3 w-3" /> {att.filename} <Download className="h-3 w-3" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-[#2A3441] pt-4 mt-4" data-testid="message-input-area">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {attachments.map((att, i) => (
              <div key={i} className="flex items-center gap-1 text-xs bg-[#0B0F19] border border-[#2A3441] rounded-lg px-2 py-1 text-[#94A3B8]">
                <FileIcon className="h-3 w-3" /> {att.filename}
                <button onClick={() => removeAttachment(i)} className="text-red-400 hover:text-red-300 ml-1"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 items-end">
          <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} data-testid="file-input" />
          <Button variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()} disabled={uploading}
            className="text-[#64748b] hover:text-white flex-shrink-0" data-testid="attach-file-btn">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
          </Button>
          <Textarea ref={textareaRef} value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyDown={handleKeyDown}
            placeholder="Ecrire un message... (Shift+Enter pour un retour a la ligne)"
            className="bg-[#0B0F19] border-[#2A3441] text-white resize-none flex-1 min-h-[44px] max-h-[300px] overflow-y-auto transition-[height] duration-100"
            style={{ height: "44px" }}
            data-testid="message-textarea" />
          <Button onClick={handleSend} disabled={sending || (!newMessage.trim() && attachments.length === 0)}
            className="bg-[#0099CC] hover:bg-[#0088B5] text-white flex-shrink-0" data-testid="send-message-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
